export class UpdateScheduler {
  private frameHandle: number | undefined;
  private timeoutHandle: ReturnType<typeof setTimeout> | undefined;
  private timeoutAt: number | undefined;
  private pendingWakeups: number[] = [];
  private disposed = false;

  constructor(private readonly update: () => void) {}

  public request(delayMs = 0): void {
    if (this.disposed) {
      return;
    }

    if (delayMs <= 0) {
      this.scheduleFrame();
      return;
    }

    this.enqueueDelayedWakeup(performance.now() + delayMs);
  }

  public dispose(): void {
    this.disposed = true;
    if (this.frameHandle !== undefined) {
      cancelAnimationFrame(this.frameHandle);
      this.frameHandle = undefined;
    }
    this.clearTimeout();
    this.pendingWakeups = [];
  }

  private enqueueDelayedWakeup(requestedAt: number): void {
    if (this.timeoutAt !== undefined && this.timeoutAt <= requestedAt) {
      this.pendingWakeups.push(requestedAt);
      return;
    }

    if (this.timeoutAt !== undefined) {
      this.pendingWakeups.push(this.timeoutAt);
    }

    this.armTimeout(requestedAt);
  }

  /** Arms (or re-arms) the sole active timeout for the earliest delayed wakeup. */
  private armTimeout(at: number): void {
    this.clearTimeout();
    this.timeoutAt = at;
    const delayMs = Math.max(0, at - performance.now());
    this.timeoutHandle = setTimeout(() => {
      this.timeoutHandle = undefined;
      this.timeoutAt = undefined;
      this.scheduleFrame();
      this.armNextPendingWakeup();
    }, delayMs);
  }

  private armNextPendingWakeup(): void {
    if (this.disposed || this.pendingWakeups.length === 0) {
      return;
    }

    const now = performance.now();
    let next: number | undefined;
    const remaining: number[] = [];

    for (const at of this.pendingWakeups) {
      if (at <= now) {
        continue;
      }
      if (next === undefined || at < next) {
        if (next !== undefined) {
          remaining.push(next);
        }
        next = at;
      } else {
        remaining.push(at);
      }
    }

    this.pendingWakeups = remaining;
    if (next !== undefined) {
      this.armTimeout(next);
    }
  }

  private scheduleFrame(): void {
    if (this.disposed || this.frameHandle !== undefined) {
      return;
    }

    this.frameHandle = requestAnimationFrame(() => {
      this.frameHandle = undefined;
      if (!this.disposed) {
        this.update();
      }
    });
  }

  private clearTimeout(): void {
    if (this.timeoutHandle !== undefined) {
      clearTimeout(this.timeoutHandle);
      this.timeoutHandle = undefined;
      this.timeoutAt = undefined;
    }
  }
}
